'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Check } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { useConvesioConnect } from '@/lib/api/integrations';
import { useCustomDomainsList, type CustomDomainItem } from '@/lib/api/domains';

// ============================================================================
// Types
// ============================================================================

const schema = z.object({
  email: z.string().email('Valid email is required'),
  password: z.string().min(1, 'Password is required'),
  integrationName: z.string().min(1, 'Integration name is required'),
  domain: z.string().url('Valid domain URL is required'),
});

type FormValues = z.infer<typeof schema>;

export interface ConvesioConnectResult {
  secretKey: string;
  integrationName: string;
  webhookSecret: string;
  testMode: boolean;
}

interface Props {
  testMode: boolean;
  onTestModeChange: (value: boolean) => void;
  onSuccess: (result: ConvesioConnectResult) => void;
  isDisabled?: boolean;
}

// ============================================================================
// Domain selector sub-component
// ============================================================================

const CUSTOM_VALUE = '__custom__';

interface DomainSelectorProps {
  domains: CustomDomainItem[];
  onSelect: (url: string) => void;
  isDisabled: boolean;
}

const DomainSelector = ({ domains, onSelect, isDisabled }: DomainSelectorProps) => {
  const [selected, setSelected] = useState(domains.length > 0 ? domains[0].id : CUSTOM_VALUE);
  const [customInput, setCustomInput] = useState('');

  const handleSelectChange = (value: string) => {
    setSelected(value);
    if (value === CUSTOM_VALUE) {
      if (customInput) onSelect(customInput);
    } else {
      const domain = domains.find((d) => d.id === value);
      if (domain) onSelect(`https://${domain.domain}`);
    }
  };

  const handleCustomInput = (value: string) => {
    setCustomInput(value);
    if (selected === CUSTOM_VALUE) onSelect(value);
  };

  return (
    <div className="space-y-2">
      <Select value={selected} onValueChange={handleSelectChange} disabled={isDisabled}>
        <SelectTrigger>
          <SelectValue placeholder="Select a domain…" />
        </SelectTrigger>
        <SelectContent>
          {domains.map((d) => (
            <SelectItem key={d.id} value={d.id}>
              {d.domain}
              {d.isVerified && <span className="ml-2 text-xs text-green-600">(verified)</span>}
            </SelectItem>
          ))}
          <SelectItem value={CUSTOM_VALUE}>Enter a custom URL…</SelectItem>
        </SelectContent>
      </Select>

      {selected === CUSTOM_VALUE && (
        <Input
          placeholder="https://your-store.com"
          value={customInput}
          onChange={(e) => handleCustomInput(e.target.value)}
          disabled={isDisabled}
        />
      )}
    </div>
  );
};

// ============================================================================
// Main component
// ============================================================================

export const ConvesioAutoConnectSection = ({ testMode, onTestModeChange, onSuccess, isDisabled = false }: Props) => {
  const { toast } = useToast();
  const connectMutation = useConvesioConnect();
  const { data: domainsData, isLoading: loadingDomains } = useCustomDomainsList();

  const domains = domainsData?.domains ?? [];

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { integrationName: 'Tagada' },
  });

  const handleDomainSelect = (url: string) => setValue('domain', url);

  const onSubmit = async (data: FormValues) => {
    try {
      const result = await connectMutation.mutateAsync({ ...data, testMode });
      toast({ title: 'Connected', description: 'Successfully connected to ConvesioPay.' });
      onSuccess({
        secretKey: result.secretKey,
        integrationName: result.connectedIntegrationName,
        webhookSecret: result.webhookSecret,
        testMode: result.testMode,
      });
    } catch (error: any) {
      toast({
        title: 'Connection failed',
        description: error?.message ?? 'Could not connect to ConvesioPay.',
        variant: 'destructive',
      });
    }
  };

  const isPending = connectMutation.isPending || isDisabled;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Auto Connect</CardTitle>
        <CardDescription>
          Sign in with your ConvesioPay account to generate credentials automatically.
          Your password is never stored.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">ConvesioPay account email <span className="text-destructive">*</span></Label>
            <Input id="email" type="email" placeholder="you@example.com" disabled={isPending} {...register('email')} />
            {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password <span className="text-destructive">*</span></Label>
            <Input id="password" type="password" placeholder="••••••••" disabled={isPending} {...register('password')} />
            {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
            <p className="text-xs text-muted-foreground">Used once to generate keys — never stored.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="integrationName">Integration name <span className="text-destructive">*</span></Label>
            <Input id="integrationName" placeholder="Tagada" disabled={isPending} {...register('integrationName')} />
            {errors.integrationName && <p className="text-xs text-destructive">{errors.integrationName.message}</p>}
            <p className="text-xs text-muted-foreground">Name of the integration as it appears in ConvesioPay.</p>
          </div>

          <div className="space-y-2">
            <Label>Store domain <span className="text-destructive">*</span></Label>
            <p className="text-xs text-muted-foreground">Select your store domain — ConvesioPay will register a webhook on it.</p>
            {loadingDomains ? (
              <Card className="p-4 space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </Card>
            ) : (
              <DomainSelector domains={domains} onSelect={handleDomainSelect} isDisabled={isPending} />
            )}
            {errors.domain && <p className="text-xs text-destructive">{errors.domain.message}</p>}
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="testMode-connect" className="font-medium">Test mode (sandbox)</Label>
              <p className="text-xs text-muted-foreground">No real charges will be made.</p>
            </div>
            <Switch
              id="testMode-connect"
              checked={testMode}
              onCheckedChange={onTestModeChange}
              disabled={isPending}
            />
          </div>

          <Button type="submit" className="w-full" disabled={isPending}>
            {connectMutation.isPending ? 'Connecting…' : 'Connect to ConvesioPay'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
